import React, { useEffect, useState, Fragment } from 'react';
import Barcode from '../Barcode/Barcode';
import classes from './OfferDetails.module.scss';
import Button from '../../UI/Button/Button';
import Loader from '../../UI/Loader/Loader';
import AppleWallet from '../../../images/add_to_apple_wallet.svg';
import { useSelector } from 'react-redux';
import { copyToClipboard } from '../../../utils/clipboard';

const OfferDetails = (props) => {
    const { hasTimer, hideAt, limitSeconds } = props;
    const [barcodeMessage, setBarcodMessage] = useState(false);
    const [showCountdown, setShowCountdown] = useState(false);
    const [copied, setCopied] = useState(false);

    const [days, setDays] = useState('');
    const [hours, setHours] = useState('');
    const [minutes, setMinutes] = useState('');
    const [seconds, setSeconds] = useState('');
    const content = useSelector((state) => state.content);
    const device = useSelector((state) => state.device);
    // Embedded mode renders inside the Buy-Now overlay's in-store section (no
    // modal chrome, no duplicate code pill — the overlay already shows the code).
    const embedded = props.embedded;
    // Apple Wallet passes install on iPhone only — same gate as the page badges.
    const canApplePass =
        device?.deviceDetails?.isMobile &&
        device?.deviceDetails?.isIphone &&
        content?.applePass;
    useEffect(()=> {
        const setZeros = (num, f) => {
            let n = String(num / 100).slice(2);
            return n.length === 0 && f?.length === 2 ? '00' : n.length === 1 ? n + '0' : n;
        }
        setDays(
            setZeros(
                Math.floor(props.distance / (1000 * 60 * 60 * 24)),
                days
            ),
        );
        setHours(
            setZeros(
                Math.floor((props.distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                hours
            )
        );
        setMinutes(
            setZeros(
                Math.floor((props.distance % (1000 * 60 * 60)) / (1000 * 60)),
                minutes
            )
        );
        setSeconds(
            setZeros(
                Math.floor((props.distance % (1000 * 60)) / 1000),
                seconds
            )
        );
    }, [days, hours, minutes, props.distance, seconds]);

    useEffect(() => {
        if (hasTimer) {
            if (hideAt !== 0 && !props.running) {
                let expDate = new Date(hideAt);
                props.onSetExpDate(expDate);
                setBarcodMessage(false);
                setShowCountdown(true);
                props.onSetRunning(true)
            } 
            if (props.running) {
                setBarcodMessage(false);
                setShowCountdown(true);
            } else {
                setShowCountdown(false);
                setBarcodMessage(true);
            }
        }
    }, [hasTimer, hideAt, limitSeconds, props])

    const handleTimerStart = () => {
        const expDate = new Date(Date.now() + limitSeconds * 1000);
        props.onSetExpDate(expDate);
        props.onSetRunning(true);
        props.onUpdateStatus(4)
        setBarcodMessage(false)
        setShowCountdown(true);
    }

    // The redemption code string. Surfaced as a copyable pill (scratcher-package
    // design) above the scannable barcode, which is kept for POS scan flows.
    const codeValue = content?.content?.redemption?.code?.value ?? '';

    const handleCopy = async () => {
        // Honest feedback: only show COPIED when the copy actually landed.
        const ok = await copyToClipboard(codeValue);
        setCopied(ok ? 'ok' : 'fail');
        setTimeout(() => setCopied(false), 1500);
    };

    let barcode = (
        <div className={classes.RedeemBlock}>
            {embedded ? (
                /* Inside the overlay the online section already shows the code +
                   COPY, so the in-store reveal is just instructions + barcode. */
                <div className={classes.CodeSub}>
                    Show this barcode to the cashier at the retailer to redeem in store.
                </div>
            ) : (
                <Fragment>
                    <div className={classes.CodeTitle}>Here&rsquo;s your Redemption Code</div>
                    <div className={classes.CodeSub}>
                        Copy the code below, or show the barcode to an associate to redeem.
                    </div>
                    {codeValue !== '' && (
                        <div className={classes.CodePill}>
                            <span className={classes.CodeText}>{codeValue}</span>
                            <button
                                type='button'
                                className={classes.CodeCopy}
                                onClick={handleCopy}
                                aria-label='Copy redemption code'>
                                {copied === 'ok' ? 'COPIED' : copied === 'fail' ? 'FAILED' : 'COPY'}
                            </button>
                        </div>
                    )}
                </Fragment>
            )}
            {
                showCountdown && (
                    <div className={classes.CountDown}>
                        <p className={classes.CountDownText}>This code is available for
                            <span>
                                {days !== '' && days+':'}{hours !== '' && hours+':'}{minutes !== '' && minutes + ':'}{seconds !== '' && seconds}
                            </span>
                        </p>
                    </div>
                )
            }
            {/* Barcode renders whenever the code is revealed — its visibility is
                no longer tied to the countdown timer. */}
            <Barcode content={content?.content} />
            {/* Add to Apple Wallet — a reminder pass (geofence + time). iPhone
                only. Lives here so both in-store entry points share it. */}
            {canApplePass && props.onAddToWallet && (
                <img
                    src={AppleWallet}
                    alt='Add to Apple Wallet'
                    className={classes.WalletBadge}
                    onClick={() => props.onAddToWallet()}
                />
            )}
        </div>
    );

    const modalIcon = (
        <div className={classes.OfferDetailsModalIconContainer}>
            <img
                className={classes.OfferDetailsModalIcon}
                src={content.content.images.thumbnail.url}
                alt='wallet_icon'
            />
        </div>
    );

    const expMessage = (
        <div>
            <p>This offer has been redeemed or is no longer available. </p>
            <p>Thank you.</p>
        </div>
    )

    return (
        <Fragment>
            {barcodeMessage ? (
                <div style={{maxWidth: '300px'}} className={classes.OfferDetailsModalContainer}>
                    <p className={classes.MessageTitle}>Ready to redeem?</p>
                    <p>This offer may only be used <strong>once</strong> and will be valid for a short time.</p>
                    <p>Please be in-person and ready to show an associate the promo code to redeem this offer.</p>
                    <div>
                        <div style={{width: '260px', margin: '24px 0'}}>
                            <Button
                                backgroundColor={'var(--a8-primary)'}
                                onClick={handleTimerStart}
                                btnText={'Show Promo Code'}
                                className={classes.Button}>
                            </Button>
                        </div>
                        <div onClick={props.onClose} className={classes.CloseLink}>
                            Not Ready
                        </div>
                    </div>
                </div>
            ) :
            
            (
                <div
                    id={'copyCodeContainer'}
                    className={`${classes.OfferDetailsModalContainer} ${embedded ? classes.Embedded : ''}`}>
                    {!embedded && modalIcon}
                    {
                        (props.expired === null && hasTimer) ?
                        <Loader style={{margin: '24px'}}/> :
                        props.expired ?
                        expMessage :
                        barcode
                    }
                </div>
            )}
        </Fragment>
    );
};

export default OfferDetails;
